import React, { useState } from "react";
import {
  Typography,
  Stack,
  Alert,
  AlertTitle,
  Box,
  Button,
  TextField,
} from "@mui/material";
import { formatDate } from "app/utils/dateUtils";
import Div from "@jumbo/shared/Div";
import JumboCodeBlock from "@jumbo/components/JumboCodeBlock";
import {
  ALERT_SEVERITIES,
  SUBMISSION_STATUSES,
} from "app/utils/constants/constants";
import LaunchIcon from "@mui/icons-material/Launch";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LoadingView from "components/LoadingView";
import formatValue from "app/utils/formatUtils";

const SubmissionPanel = ({
  isPolling,
  submissionStatus,
  theme,
  codeConstants,
  metadata,
  selectedSubmission,
  onCopyCode,
  onSwitchToCodeTab,
}) => {
  const [copyButtonText, setCopyButtonText] = useState("Bring to editor");

  // Use selectedSubmission if available, otherwise fall back to submissionStatus
  const activeSubmission = selectedSubmission || submissionStatus;

  // Resolve the display status from metadata.submissionStatus
  const displayStatus = activeSubmission
    ? metadata?.submissionStatus?.find(
        (status) => status.key === activeSubmission.status
      )?.value || activeSubmission.status
    : "Unknown";

  // Handle copy code action with delay
  const handleCopyCode = async () => {
    if (!activeSubmission?.codeSnippet) return;

    try {
      await navigator.clipboard.writeText(activeSubmission.codeSnippet);
      setCopyButtonText("Copied");

      // Find the language object from metadata
      const language = metadata?.programmingLanguages?.find(
        (lang) => lang.key === activeSubmission.programmingLanguage
      );

      if (language) {
        // Pass both code and language
        onCopyCode({
          code: activeSubmission.codeSnippet,
          language,
        });
      } else {
        console.warn(
          `No valid language found for key: ${activeSubmission.programmingLanguage}`
        );
      }

      // Delay switching to Code tab by 500ms
      setTimeout(() => {
        onSwitchToCodeTab();
        setCopyButtonText("Bring to editor");
      }, 500);
    } catch (error) {
      console.error("Failed to copy code:", error);
      setCopyButtonText("Bring to editor");
    }
  };

  return (
    <Div
      sx={{
        height: "100%",
        overflow: "auto",
        bgcolor: theme.palette.background.paper,
      }}
    >
      {isPolling || !activeSubmission ? (
        <Div
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            height: "100%",
          }}
        >
          <LoadingView message="Processing submission..." size={30} />
        </Div>
      ) : activeSubmission ? (
        <Stack spacing={2} sx={{ p: 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body2" color="text.secondary">
              {activeSubmission.submittedOn
                ? `Submitted on ${formatDate(activeSubmission.submittedOn)}`
                : "Submission time unavailable"}
            </Typography>

            <Button
              variant="text"
              startIcon={
                copyButtonText === "Bring to editor" ? (
                  <LaunchIcon />
                ) : (
                  <CheckCircleIcon />
                )
              }
              onClick={handleCopyCode}
              aria-label="Copy code to editor"
              size="small"
              sx={{
                textTransform: "none",
              }}
            >
              {copyButtonText}
            </Button>
          </Stack>

          {activeSubmission.status === SUBMISSION_STATUSES.ACCEPTED ? (
            <Alert severity={ALERT_SEVERITIES.SUCCESS}>
              <AlertTitle>Success</AlertTitle>
              Your code has been accepted
            </Alert>
          ) : (
            <Alert
              severity={ALERT_SEVERITIES.ERROR}
              sx={{ width: "100%", "& .MuiAlert-message": { width: "100%" } }}
            >
              <AlertTitle>{displayStatus}</AlertTitle>
              {activeSubmission.compilationOutput && (
                <Typography variant="body1">
                  <TextField
                    multiline
                    id="outlined-basic"
                    variant="outlined"
                    label="Compilation Output"
                    value={activeSubmission.compilationOutput}
                    disabled
                    fullWidth
                    sx={{
                      "& .MuiInputLabel-root.Mui-disabled": {
                        color: "#475259",
                      },
                      "& .MuiInputBase-input.MuiOutlinedInput-input.Mui-disabled":
                        {
                          textFillColor: "#475259",
                        },
                    }}
                  />
                </Typography>
              )}
              {activeSubmission.consoleOutput && (
                <Typography variant="body1">
                  <TextField
                    multiline
                    id="outlined-basic"
                    variant="outlined"
                    label="Console Output"
                    value={activeSubmission.consoleOutput}
                    disabled
                    fullWidth
                    sx={{
                      "& .MuiInputLabel-root.Mui-disabled": {
                        color: "#475259",
                      },
                      "& .MuiInputBase-input.MuiOutlinedInput-input.Mui-disabled":
                        {
                          textFillColor: "#475259",
                        },
                    }}
                  />
                </Typography>
              )}
              {activeSubmission.consoleError && (
                <Typography variant="body1">
                  <TextField
                    multiline
                    id="outlined-basic"
                    variant="outlined"
                    label="Console Error"
                    value={activeSubmission.consoleError}
                    disabled
                    fullWidth
                    sx={{
                      "& .MuiInputLabel-root.Mui-disabled": {
                        color: "#475259",
                      },
                      "& .MuiInputBase-input.MuiOutlinedInput-input.Mui-disabled":
                        {
                          textFillColor: "#475259",
                        },
                    }}
                  />
                </Typography>
              )}
            </Alert>
          )}

          {/* Code Snippet */}
          {activeSubmission.codeSnippet && (
            <JumboCodeBlock
              text={activeSubmission.codeSnippet}
              language={
                codeConstants[activeSubmission.programmingLanguage]?.language ||
                "text"
              }
              showLineNumbers={true}
            />
          )}
          {/* Test Cases or Success Alert */}
          {activeSubmission.status !== SUBMISSION_STATUSES.ACCEPTED && (
            <>
              <Typography variant="h6" sx={{ mt: 2 }}>
                <strong>Test Cases:</strong>{" "}
                {activeSubmission.numberOfSuccessfulTests !== undefined &&
                  activeSubmission.totalNumberOfTests !== undefined &&
                  `${activeSubmission.numberOfSuccessfulTests}/${activeSubmission.totalNumberOfTests} testcases passed`}
              </Typography>

              {/* First Failed Test Case */}
              {activeSubmission.failedTests?.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" sx={{ mt: 1, mb: 1 }}>
                    Failed Tests:
                  </Typography>
                  {activeSubmission.failedTests
                    .slice(0, 1)
                    .map((test, index) => (
                      <Alert
                        key={`failed-${index}`}
                        severity={ALERT_SEVERITIES.ERROR}
                        sx={{
                          width: "100%",
                          "& .MuiAlert-message": { width: "100%" },
                        }}
                      >
                        <AlertTitle sx={{ mb: 2 }}>
                          Test Case {index + 1}
                        </AlertTitle>
                        <Stack>
                          {/* Inputs */}
                          {test.problemInputs?.length > 0 && (
                            <Box>
                              <Typography fontWeight="600" sx={{ mb: 2 }}>
                                Inputs:
                              </Typography>
                              {test.problemInputs.map((input, inputIndex) => (
                                <Div
                                  key={inputIndex}
                                  display="flex"
                                  sx={{ mb: 2 }}
                                >
                                  <TextField
                                    key={inputIndex}
                                    id="outlined-basic"
                                    label={input.variableName}
                                    variant="outlined"
                                    value={formatValue(input.value)}
                                    disabled
                                    fullWidth
                                    sx={{
                                      "& .MuiInputLabel-root.Mui-disabled": {
                                        color: "#475259",
                                        fontSize: "1rem",
                                        left: "-2px",
                                        top: "-1px",
                                      },
                                      "& .MuiInputBase-input.MuiOutlinedInput-input.Mui-disabled":
                                        {
                                          textFillColor: "#475259",
                                        },
                                    }}
                                  />
                                </Div>
                              ))}
                            </Box>
                          )}

                          {/* Expected Output */}
                          <Box>
                            <Typography fontWeight="600" sx={{ mb: 2 }}>
                              Expected Output:
                            </Typography>
                            <Typography variant="body2">
                              {test.problemOutputs.map(
                                (output, outputIndex) => (
                                  <Div
                                    key={outputIndex}
                                    display="flex"
                                    sx={{ mb: 2 }}
                                  >
                                    <TextField
                                      key={outputIndex}
                                      id="outlined-basic"
                                      label={output.variableName}
                                      variant="outlined"
                                      value={formatValue(output.value)}
                                      disabled
                                      fullWidth
                                      sx={{
                                        "& .MuiInputLabel-root.Mui-disabled": {
                                          color: "#475259",
                                          fontSize: "1rem",
                                          left: "-2px",
                                          top: "-1px",
                                        },
                                        "& .MuiInputBase-input.MuiOutlinedInput-input.Mui-disabled":
                                          {
                                            textFillColor: "#475259",
                                          },
                                      }}
                                    />
                                  </Div>
                                )
                              )}
                            </Typography>
                          </Box>

                          {/* Actual Output */}
                          <Box>
                            <Typography fontWeight="600" sx={{ mb: 1 }}>
                              Actual Output:
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <TextField
                                id="outlined-basic"
                                variant="outlined"
                                value={test.executionOutput}
                                fullWidth
                                disabled
                                sx={{
                                  "& .MuiInputLabel-root.Mui-disabled": {
                                    color: "#475259",
                                  },
                                  "& .MuiInputBase-input.MuiOutlinedInput-input.Mui-disabled":
                                    {
                                      textFillColor: "#475259",
                                    },
                                }}
                              />
                            </Typography>
                          </Box>

                          {/* Console Output */}
                          {test.stdOut && (
                            <>
                              <Typography fontWeight="600" sx={{ mb: 1 }}>
                                Execution logs:
                              </Typography>
                              <TextField
                                multiline
                                id="outlined-basic"
                                variant="outlined"
                                aria-label="Execution logs"
                                value={test.stdOut}
                                disabled
                                fullWidth
                                sx={{
                                  "& .MuiInputLabel-root.Mui-disabled": {
                                    color: "#475259",
                                  },
                                  "& .MuiInputBase-input.MuiOutlinedInput-input.Mui-disabled":
                                    {
                                      textFillColor: "#475259",
                                    },
                                }}
                              />
                            </>
                          )}
                        </Stack>
                      </Alert>
                    ))}
                </Box>
              )}
            </>
          )}
          {/* Compilation or Runtime Errors */}
          {activeSubmission.compile_output && (
            <Alert severity="error">
              <AlertTitle>Compilation Output</AlertTitle>
              <pre>{activeSubmission.compile_output}</pre>
            </Alert>
          )}
          {activeSubmission.stderr && (
            <Alert severity="error">
              <AlertTitle>Runtime Error</AlertTitle>
              <pre>{activeSubmission.stderr}</pre>
            </Alert>
          )}
        </Stack>
      ) : (
        <Div
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            height: "100%",
          }}
        >
          <LoadingView message="Processing submission..." size={30} />
        </Div>
      )}
    </Div>
  );
};

export default SubmissionPanel;
